/**
 * Canonical `unknown → ToolErrorResponse` normaliser.
 *
 * The `<Tool>` execute boundary (and any other call site that catches a
 * thrown tool error) routes through here so a rejected `execute` always
 * reaches the transcript as the structured failure envelope
 * (`{ success: false, errorType, error, suggestions?, details?,
 * recovery? }`) that `parseResultEnvelope` discriminates on. Without
 * this, a raw thrown error lands on the tool part with no `success:
 * false` marker, is misclassified as a success, and is silently dropped
 * from the LLM transcript.
 *
 * Single source of truth: `ToolExecutionError` instances render
 * themselves via `toStructuredResponse()`; anything else is wrapped in
 * the canonical {@link UnexpectedError} so the envelope shape and
 * recovery hint are produced in exactly one place.
 */

import { ExprError } from '#core/lib/expr/errors.ts';

import { type ToolErrorResponse, ToolExecutionError } from './base.ts';
import { UnexpectedError } from './environment.ts';
import { InvalidExpressionError } from './invalid-input.ts';

export function toToolErrorResponse(error: unknown): ToolErrorResponse {
  if (error instanceof ToolExecutionError) {
    return error.toStructuredResponse();
  }
  // Engine s-expr failures (parse / unknown column / type mismatch / …)
  // are LLM-fixable input errors, not fatal internal bugs — map them to
  // the retryable `InvalidExpressionError` so the loop self-corrects
  // rather than being told to report a bug.
  if (error instanceof ExprError) {
    return new InvalidExpressionError(error).toStructuredResponse();
  }
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown tool execution error.';
  return new UnexpectedError(message).toStructuredResponse();
}
